import { useEffect, useRef } from "react";

interface CalendlyPrefill {
  name?: string;
  email?: string;
  customAnswers?: {
    a1?: string;
    a2?: string;
    a3?: string;
  };
}

interface CalendlyEmbedProps {
  url: string;
  prefill?: CalendlyPrefill;
}

interface CalendlyAPI {
  initInlineWidget: (opts: {
    url: string;
    parentElement: HTMLElement | null;
    prefill?: CalendlyPrefill;
    utm?: Record<string, string>;
  }) => void;
}

const SCRIPT_SRC = "https://assets.calendly.com/assets/external/widget.js";
const STYLE_HREF = "https://assets.calendly.com/assets/external/widget.css";

function ensureCalendlyAssets(): Promise<void> {
  return new Promise((resolve) => {
    // CSS — injected once, harmless to load before script.
    if (!document.querySelector(`link[href="${STYLE_HREF}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = STYLE_HREF;
      document.head.appendChild(link);
    }

    // Script — inject and resolve on load (or immediately if already loaded).
    const win = window as unknown as { Calendly?: CalendlyAPI };
    if (win.Calendly) {
      resolve();
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${SCRIPT_SRC}"]`,
    );
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.addEventListener("load", () => resolve(), { once: true });
    document.head.appendChild(script);
  });
}

export function CalendlyEmbed({ url, prefill }: CalendlyEmbedProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;

    void ensureCalendlyAssets().then(() => {
      if (cancelled) return;
      const Calendly = (window as unknown as { Calendly?: CalendlyAPI }).Calendly;
      if (!Calendly || !containerRef.current) return;
      containerRef.current.innerHTML = "";
      Calendly.initInlineWidget({
        url,
        parentElement: containerRef.current,
        prefill: prefill || {},
        utm: {},
      });
    });

    return () => {
      cancelled = true;
    };
  }, [url, prefill]);

  return (
    <div
      ref={containerRef}
      title="Schedule a discovery call"
      style={{
        minWidth: 320,
        height: 700,
        borderRadius: 16,
        overflow: "hidden",
        background: "var(--dyna-bg-elevated)",
        border: "1px solid var(--dyna-border)",
      }}
    />
  );
}
